import type { AnyValue } from "@/lib/rowTypes";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Briefcase, CheckCircle2, Target, Coins, Sparkles,
  BookOpen, AlertTriangle, Layers, Wallet,
} from "lucide-react";

import ModuleShell from "@/components/modules/ModuleShell";
import ModuleStepCard from "@/components/modules/ModuleStepCard";
import ModuleQuiz from "@/components/modules/ModuleQuiz";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription,
} from "@/components/ui/sheet";
import { toast } from "sonner";

import { useCurriculumProgress } from "@/hooks/useCurriculumProgress";
import { useModuleRewards } from "@/hooks/useModuleRewards";
import { useAccounts } from "@/hooks/useAccounts";
import { useAuth } from "@/hooks/useAuth";
import { useQAOverride } from "@/hooks/useQAOverride";
import { useCareerActivation } from "@/hooks/useCareerActivation";
import { supabase } from "@/integrations/supabase/client";
import { useRef } from "react";
import {
  newAttemptId, startModuleAttempt, commitModuleAttempt, trackModule, trackAccountEffect,
} from "@/lib/learning/telemetry";
import { CAREER_EVENTS, MODULE_CONCEPT_TAGS } from "@/lib/learning/moduleEvents";

import {
  CAREERS, CAREER_DECISIONS, SIDE_HUSTLES, SIDE_HUSTLE_DECISIONS,
  CAREER_KEY_TERMS, FIT_LABELS, computeFit, initialSideHustleMetrics,
  type CareerFitScore,
} from "@/data/careerPathBank";
import { buildWeeklyPaycheck } from "@/lib/payroll/weeklyPayroll";

/* ========================================================================== */
/* Career Path Simulator                                                       */
/* ─────────────────────────────────────────────────────────────────────────── */
/* - Choice + side-hustle decisions are stored locally during the run.         */
/* - On quiz pass we persist a career_module_attempts row and call             */
/*   apply_career_path_attempt (idempotent, server-side). That RPC deducts     */
/*   startup costs from checking, activates learner_career_state, and is the   */
/*   only path that unlocks paychecks + future career events.                  */
/* - Quiz fail → ModuleRemediationScreen kicks in inside ModuleQuiz. No        */
/*   career state is written, no startup cost is taken.                        */
/* - Revisits with an existing learner_career_state show the locked summary    */
/*   (or the QA preview banner for admin replays).                             */
/* ========================================================================== */

type Step =
  | "intro"
  | "career_choice"
  | "career_decision"
  | "side_hustle_choice"
  | "side_hustle_decision"
  | "review"
  | "quiz"
  | "summary";

const STEP_ORDER: Step[] = [
  "intro", "career_choice", "career_decision", "side_hustle_choice",
  "side_hustle_decision", "review", "quiz", "summary",
];

const CareerPathModule = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isQAOverride } = useQAOverride();
  const { moduleGates, loading: progressLoading } = useCurriculumProgress();
  const { completeModule } = useModuleRewards();
  const { accounts, refetch: refetchAccounts } = useAccounts();
  const { state: existingState, activated, loading: stateLoading, refetch: refetchActivation } = useCareerActivation();

  const [step, setStep] = useState<Step>("intro");
  const [careerKey, setCareerKey] = useState<string | null>(null);
  const [careerChoiceKey, setCareerChoiceKey] = useState<string | null>(null);
  const [sideHustleKey, setSideHustleKey] = useState<string | null>(null);
  const [sideHustleChoiceKey, setSideHustleChoiceKey] = useState<string | null>(null);
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [keyTermsOpen, setKeyTermsOpen] = useState(false);
  const attemptRef = useRef(newAttemptId("career_path"));
  const startedRef = useRef(false);
  const startTelemetry = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    void startModuleAttempt({
      moduleSlug: "career_path",
      attemptId: attemptRef.current,
      conceptTags: MODULE_CONCEPT_TAGS.career_path,
    });
  };

  const gate = moduleGates.find(g => g.moduleKey === "career_path");
  const isLocked = !gate?.isUnlocked;
  const lockMsg = gate?.missingPrereqs?.length
    ? `Complete: ${gate.missingPrereqs.join(", ")}`
    : "Complete the prior module to unlock.";

  if (progressLoading || stateLoading) return null;

  const career = careerKey ? CAREERS.find(c => c.key === careerKey)! : null;
  const careerDecision = careerKey ? CAREER_DECISIONS[careerKey] : null;
  const selectedCareerChoice = careerDecision?.choices.find(c => c.key === careerChoiceKey) || null;
  const sideHustle = sideHustleKey ? SIDE_HUSTLES.find(s => s.key === sideHustleKey) || null : null;
  const sideHustleDecision = sideHustleKey && sideHustleKey !== "none" ? SIDE_HUSTLE_DECISIONS[sideHustleKey] : null;
  const selectedSideChoice = sideHustleDecision?.choices.find(c => c.key === sideHustleChoiceKey) || null;

  const startupTotal =
    (selectedCareerChoice?.startupCost || 0) +
    (selectedSideChoice?.startupCost || 0) +
    (sideHustle && sideHustle.key === "passive_investing" ? 0 : 0); // captured via the decision

  const projectedFit: CareerFitScore | null = career
    ? computeFit(career.baselineFit, selectedCareerChoice?.fitDelta || {}, selectedSideChoice?.fitDelta || {})
    : null;

  const checking = accounts.find(a => a.account_type === "checking");
  const checkingBalance = checking ? Number(checking.balance) : 0;
  const insufficientFunds = startupTotal > 0 && startupTotal > checkingBalance;

  /* ─── Step navigation ─── */

  const goNextFromSideHustle = () => {
    if (sideHustleKey === "none") {
      setSideHustleChoiceKey(null);
      setStep("review");
    } else {
      setStep("side_hustle_decision");
    }
  };

  /* ─── Persist attempt + apply on pass ─── */

  const ensureAttempt = async (): Promise<string | null> => {
    if (!user || !career) return null;
    if (attemptId) return attemptId;
    startTelemetry();
    void trackModule({
      eventType: CAREER_EVENTS.PATH_SELECTED,
      moduleSlug: "career_path",
      attemptId: attemptRef.current,
      metadata: {
        selected_career: career.key,
        compensation_type: career.compType,
        annual_salary: career.annualIncome,
        hourly_rate: career.hourlyRate ?? null,
        daily_gross_pay: career.dailyGross,
      },
    });
    if (selectedCareerChoice) {
      void trackModule({
        eventType: CAREER_EVENTS.TRADEOFF,
        moduleSlug: "career_path",
        attemptId: attemptRef.current,
        metadata: { decision_choice: selectedCareerChoice.key, skill_investment_cost: selectedCareerChoice.startupCost ?? 0 },
      });
    }
    if (sideHustleKey && sideHustleKey !== "none") {
      void trackModule({
        eventType: CAREER_EVENTS.SIDE_HUSTLE,
        moduleSlug: "career_path",
        attemptId: attemptRef.current,
        metadata: { selected_side_hustle: sideHustleKey, side_hustle_startup_cost: selectedSideChoice?.startupCost ?? 0 },
      });
      if ((selectedSideChoice?.startupCost ?? 0) > 0) {
        void trackModule({
          eventType: CAREER_EVENTS.SIDE_HUSTLE_STARTUP,
          moduleSlug: "career_path",
          attemptId: attemptRef.current,
          metadata: { side_hustle_startup_cost: selectedSideChoice?.startupCost ?? 0 },
        });
      }
    }

    // Pay figures are advisory only: the server resolves comp type, income, hourly
    // rate, weekly hours and startup costs from the career catalog on activation.
    const fitForServer = {
      ...(projectedFit || {}),
      displayed_comp_type: career.compType,
      displayed_annual_income: career.annualIncome,
      side_hustle_metrics: sideHustleKey && sideHustleKey !== "none"
        ? initialSideHustleMetrics(sideHustleKey) : {},
    };


    const { data, error } = await supabase
      .from("career_module_attempts")
      .insert({
        user_id: user.id,
        career_key: career.key,
        career_choice_key: careerChoiceKey,
        side_hustle_key: sideHustleKey,
        side_hustle_choice_key: sideHustleChoiceKey,
        career_fit_score: fitForServer,
        startup_costs_total: startupTotal,
        passed: false,
        applied: false,
      })
      .select("id")
      .single();

    if (error || !data) {
      console.warn("[career_path] could not create attempt", error);
      return null;
    }
    setAttemptId((data).id);
    return (data).id;
  };

  const handlePass = async () => {
    setSubmitting(true);
    try {
      // QA admins: never write learner data; let ModuleRewards toast handle the preview.
      if (isQAOverride) {
        await completeModule("career_path");
        setStep("summary");
        return;
      }

      const id = await ensureAttempt();
      if (id) {
        // `passed` is money-bearing: the server grants it only when a passing
        // module_completions row already exists for this learner.
        await completeModule("career_path");
        await supabase.rpc("pass_career_module_attempt", { p_attempt_id: id });
        const { data: applyRes } = await supabase.rpc("apply_career_path_attempt", { p_attempt_id: id });

        const skipped = (applyRes as AnyValue)?.startup_costs_skipped;
        if (skipped) {
          toast.warning("Startup costs skipped — checking didn't have enough", {
            description: "Your career path activated, but the startup deduction was not taken.",
          });
        }
      }
      await completeModule("career_path");
      await Promise.all([refetchActivation(), refetchAccounts()]);
      void trackModule({
        eventType: CAREER_EVENTS.FINAL_OUTCOME,
        moduleSlug: "career_path",
        attemptId: attemptRef.current,
        metadata: {
          selected_career: career?.key,
          annual_salary: career?.annualIncome,
          daily_gross_pay: career?.dailyGross,
          selected_side_hustle: sideHustleKey,
          career_growth_score: projectedFit?.growth_potential ?? null,
          stability_score: projectedFit?.stability ?? null,
          risk_score: projectedFit?.risk ?? null,
        },
      });
      void trackAccountEffect({
        moduleSlug: "career_path",
        attemptId: attemptRef.current,
        eventType: "paycheck_allocated",
        status: "committed",
        amount: career?.dailyGross ?? null,
        account: "checking",
        metadata: { selected_career: career?.key },
      });
      void commitModuleAttempt({
        moduleSlug: "career_path",
        attemptId: attemptRef.current,
        metadata: { selected_career: career?.key },
      });
      setStep("summary");
    } catch (err) {
      console.error("[career_path] failed to apply attempt", err);
      toast.error("Could not activate career path. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ─── Render: revisit-completed summary ─── */

  const showCompleted = activated && !isQAOverride;

  return (
    <ModuleShell
      title="Career Path"
      stepIndex={STEP_ORDER.indexOf(step)}
      totalSteps={STEP_ORDER.length}
      isLocked={isLocked}
      lockMessage={lockMsg}
      isCompleted={showCompleted}
      completedMessage="Your career path is active. Daily paychecks arrive at your next check-in. View details in the Financial Command Center."
      completedAction={
        <div className="flex flex-col gap-2 w-full">
          <Button onClick={() => navigate("/finances")} className="w-full">View Command Center</Button>
          <Button variant="outline" onClick={() => navigate("/dashboard")} className="w-full">Back to Dashboard</Button>
        </div>
      }
      stepKey={step}
    >
      <div className="space-y-5">
        {/* In-module Key Terms — drawer; never navigates away */}
        <div className="flex justify-end">
          <Sheet open={keyTermsOpen} onOpenChange={setKeyTermsOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1.5 text-xs">
                <BookOpen className="h-3.5 w-3.5" /> Key Terms
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
              <SheetHeader>
                <SheetTitle>Key Terms</SheetTitle>
                <SheetDescription>Quick reference. Closing returns you to the same step.</SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-3">
                {CAREER_KEY_TERMS.map(t => (
                  <div key={t.term} className="rounded-xl border border-border/60 bg-card/40 p-3">
                    <p className="font-heading text-sm font-bold text-foreground">{t.term}</p>
                    <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{t.definition}</p>
                  </div>
                ))}
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {step === "intro" && (
          <ModuleStepCard
            icon={<Briefcase className="w-8 h-8 text-primary" />}
            title="Pick a career path that fits"
            body="Every career has tradeoffs. There is no single 'best' answer — only the path that fits the life you want to build. Your choice activates a daily paycheck, real deductions, and future events."
            highlights={[
              "Compensation type matters: salary, hourly, commission, contract",
              "Daily paychecks start after you pass the knowledge check",
              "Failed attempts apply nothing — you must pass to activate",
            ]}
            action={
              <Button onClick={() => setStep("career_choice")} className="w-full gap-2">
                Let's Begin <ArrowRight className="w-4 h-4" />
              </Button>
            }
          />
        )}
        {step === "intro" && (
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4 flex gap-3 items-start">
            <Wallet className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div className="text-xs leading-relaxed text-muted-foreground">
              <p className="font-semibold text-foreground mb-1">Powers your Sim Wallet paycheck</p>
              Your career choice will become your active paycheck source in HeirWise.
              Once completed, weekly paychecks will unlock in your Finance Center.
            </div>
          </div>
        )}

        {step === "career_choice" && (
          <div className="space-y-5">
            <ModuleStepCard
              icon={<Target className="w-8 h-8 text-primary" />}
              title="Choose a career"
              body="Each card shows compensation type, gross pay before taxes, and real tradeoffs. None is universally best — pick the one whose tradeoffs you want to manage."
              action={<div />}
            />
            <div className="grid gap-3">
              {CAREERS.map(c => {
                const active = careerKey === c.key;
                const isHourly = c.compType === "hourly" || c.compType === "contract";
                return (
                  <button key={c.key}
                    onClick={() => setCareerKey(c.key)}
                    className={`rounded-2xl border p-4 text-left transition-all ${
                      active ? "border-primary bg-primary/10" : "border-border bg-card/50 hover:border-primary/40"
                    }`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-heading text-sm font-bold text-foreground">{c.title}</p>
                        <p className="mt-0.5 text-[11px] uppercase tracking-wider text-primary/80">{c.compLabel}</p>
                      </div>
                      <div className="text-right shrink-0">
                        {isHourly ? (
                          <>
                            <p className="font-mono text-sm text-foreground">${c.hourlyRate}/hr</p>
                            <p className="text-[10px] text-muted-foreground">~{c.weeklyHours} hrs/wk</p>
                          </>
                        ) : (
                          <p className="font-mono text-sm text-foreground">${c.annualIncome.toLocaleString()}/yr</p>
                        )}
                        <p className="text-[10px] text-muted-foreground mt-0.5">~${c.dailyGross.toFixed(2)}/day gross</p>
                      </div>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">{c.summary}</p>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                      <div className="rounded-md bg-emerald-500/10 px-2 py-1.5">
                        <p className="text-emerald-400 font-semibold text-[10px] mb-0.5">Pros</p>
                        <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                          {c.pros.map(p => <li key={p}>{p}</li>)}
                        </ul>
                      </div>
                      <div className="rounded-md bg-destructive/10 px-2 py-1.5">
                        <p className="text-destructive font-semibold text-[10px] mb-0.5">Cons</p>
                        <ul className="list-disc list-inside text-muted-foreground space-y-0.5">
                          {c.cons.map(p => <li key={p}>{p}</li>)}
                        </ul>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <Button onClick={() => setStep("career_decision")} disabled={!careerKey} className="w-full gap-2">
              Continue <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {step === "career_decision" && careerDecision && (
          <div className="space-y-5">
            <ModuleStepCard
              icon={<Sparkles className="w-8 h-8 text-primary" />}
              title="Your first major move"
              body={careerDecision.prompt}
              action={<div />}
            />
            <div className="grid gap-3">
              {careerDecision.choices.map(ch => {
                const active = careerChoiceKey === ch.key;
                return (
                  <button key={ch.key}
                    onClick={() => setCareerChoiceKey(ch.key)}
                    className={`rounded-2xl border p-4 text-left transition-all ${
                      active ? "border-primary bg-primary/10" : "border-border bg-card/50 hover:border-primary/40"
                    }`}>
                    <p className="font-heading text-sm font-bold text-foreground">{ch.label}</p>
                    {active && (
                      <ul className="mt-2 space-y-1 text-[11px] text-muted-foreground list-disc list-inside">
                        {ch.effects.map(e => <li key={e}>{e}</li>)}
                      </ul>
                    )}
                  </button>
                );
              })}
            </div>
            <Button onClick={() => setStep("side_hustle_choice")} disabled={!careerChoiceKey} className="w-full gap-2">
              Continue <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {step === "side_hustle_choice" && (
          <div className="space-y-5">
            <ModuleStepCard
              icon={<Layers className="w-8 h-8 text-accent" />}
              title="Add a side hustle?"
              body="Side hustles cost real money to start and ongoing money to run. They don't create income automatically. Pick one — or skip."
              action={<div />}
            />
            <div className="grid gap-3">
              {SIDE_HUSTLES.map(s => {
                const active = sideHustleKey === s.key;
                return (
                  <button key={s.key}
                    onClick={() => { setSideHustleKey(s.key); setSideHustleChoiceKey(null); }}
                    className={`rounded-2xl border p-4 text-left transition-all ${
                      active ? "border-accent bg-accent/10" : "border-border bg-card/50 hover:border-accent/40"
                    }`}>
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-heading text-sm font-bold text-foreground">{s.title}</p>
                      {s.key !== "none" && (
                        <div className="text-right shrink-0 text-[10px] text-muted-foreground">
                          <p>Startup ${s.startupCost}</p>
                          <p>Monthly ${s.monthlyCarryingCost}</p>
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{s.blurb}</p>
                    {s.key !== "none" && (
                      <p className="mt-2 text-[10px] uppercase tracking-wider text-accent/80">
                        Metric: {s.metricLabel}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>
            <Button onClick={goNextFromSideHustle} disabled={!sideHustleKey} className="w-full gap-2">
              Continue <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {step === "side_hustle_decision" && sideHustleDecision && (
          <div className="space-y-5">
            <ModuleStepCard
              icon={<Coins className="w-8 h-8 text-accent" />}
              title="Launch plan"
              body={sideHustleDecision.prompt}
              action={<div />}
            />
            <div className="grid gap-3">
              {sideHustleDecision.choices.map(ch => {
                const active = sideHustleChoiceKey === ch.key;
                return (
                  <button key={ch.key}
                    onClick={() => setSideHustleChoiceKey(ch.key)}
                    className={`rounded-2xl border p-4 text-left transition-all ${
                      active ? "border-accent bg-accent/10" : "border-border bg-card/50 hover:border-accent/40"
                    }`}>
                    <p className="font-heading text-sm font-bold text-foreground">{ch.label}</p>
                    {active && (
                      <ul className="mt-2 space-y-1 text-[11px] text-muted-foreground list-disc list-inside">
                        {ch.effects.map(e => <li key={e}>{e}</li>)}
                      </ul>
                    )}
                  </button>
                );
              })}
            </div>
            <Button onClick={() => setStep("review")} disabled={!sideHustleChoiceKey} className="w-full gap-2">
              Continue <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {step === "review" && career && projectedFit && (
          <div className="space-y-5">
            <ModuleStepCard
              icon={<Sparkles className="w-8 h-8 text-primary" />}
              title="Your career blueprint"
              body={`Career: ${career.title} (${career.compLabel}). Side hustle: ${sideHustle?.title ?? "None"}.`}
              action={<div />}
            />
            <div className="rounded-2xl border border-border bg-card/50 p-4 space-y-3">
              <p className="text-[11px] uppercase tracking-wider text-primary/80 font-semibold">Career fit score</p>
              <div className="grid grid-cols-1 gap-1.5">
                {(Object.keys(FIT_LABELS) as (keyof CareerFitScore)[]).map(k => (
                  <div key={k} className="flex items-center gap-2">
                    <span className="w-32 text-[11px] text-muted-foreground">{FIT_LABELS[k]}</span>
                    <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div className="h-full bg-primary" style={{ width: `${projectedFit[k]}%` }} />
                    </div>
                    <span className="w-8 text-right font-mono text-[10px] text-muted-foreground">
                      {Math.round(projectedFit[k])}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-border bg-card/50 p-4 space-y-2 text-xs">
              <p className="font-semibold text-foreground">Startup costs on pass</p>
              <p className="text-muted-foreground">
                Total: <span className="font-mono text-foreground">${startupTotal.toFixed(2)}</span> ·
                Checking: <span className="font-mono text-foreground">${checkingBalance.toFixed(2)}</span>
              </p>
              {insufficientFunds && (
                <p className="flex items-start gap-1.5 text-amber-400">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  Checking can't cover startup costs. Your career will still activate, but the deduction will be skipped.
                </p>
              )}
              <p className="text-muted-foreground">
                Daily gross paycheck (before taxes):
                <span className="font-mono text-foreground"> ~${career.dailyGross.toFixed(2)}</span>
              </p>
            </div>

            {career.annualIncome > 0 && (() => {
              const preview = buildWeeklyPaycheck(career.annualIncome);
              return (
                <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4 space-y-2 text-xs">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-4 h-4 text-primary" />
                    <p className="font-semibold text-foreground">Weekly paycheck preview</p>
                    <span className="ml-auto text-[10px] uppercase tracking-wider text-muted-foreground">Estimated for simulation</span>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-muted-foreground">
                    <span>Annual salary</span>
                    <span className="font-mono text-foreground text-right">${preview.annualSalary.toLocaleString()}</span>
                    <span>Weekly gross</span>
                    <span className="font-mono text-foreground text-right">${preview.weeklyGrossPay.toFixed(2)}</span>
                    <span>Taxes (~{Math.round(preview.estimatedTaxRate * 100)}%)</span>
                    <span className="font-mono text-foreground text-right">-${preview.taxesWithheld.toFixed(2)}</span>
                    <span>Benefits deduction</span>
                    <span className="font-mono text-foreground text-right">-${preview.benefitsDeduction.toFixed(2)}</span>
                    <span className="font-semibold text-foreground pt-1 border-t border-border/40 mt-1">Estimated take-home</span>
                    <span className="font-mono text-primary text-right font-semibold pt-1 border-t border-border/40 mt-1">${preview.netPay.toFixed(2)}</span>
                  </div>
                </div>
              );
            })()}

            <Button
              onClick={async () => { await ensureAttempt(); setStep("quiz"); }}
              className="w-full gap-2"
              disabled={submitting}
            >
              Take Knowledge Check <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {step === "quiz" && (
          <ModuleQuiz moduleKey="career_path" onPass={handlePass} sampleSize={5} shuffleOptions />
        )}

        {step === "summary" && career && (
          <div className="space-y-5 text-center">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto border border-primary/20">
              <CheckCircle2 className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground">Career Activated</h2>
            <p className="text-sm text-muted-foreground">
              You activated <span className="text-foreground font-semibold">{career.title}</span>
              {sideHustle && sideHustle.key !== "none" ? ` with a ${sideHustle.title.toLowerCase()} side hustle` : ""}.
              Your first daily paycheck arrives at your next check-in.
            </p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => navigate("/finances")} className="w-full">View Command Center</Button>
              <Button variant="outline" onClick={() => navigate("/dashboard")} className="w-full">
                Back to Dashboard
              </Button>
            </div>

            {career.annualIncome > 0 && (() => {
              const preview = buildWeeklyPaycheck(career.annualIncome);
              return (
                <div className="rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 to-accent/5 p-5 text-left space-y-3">
                  <div className="flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-primary" />
                    <p className="font-heading text-sm font-bold text-foreground">Career Paycheck Unlocked</p>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Your selected career is now connected to your Sim Wallet. Weekly paychecks
                    will appear in the Finance Center based on your estimated take-home pay of
                    {" "}<span className="font-mono text-foreground font-semibold">${preview.netPay.toFixed(2)}/wk</span>.
                  </p>
                  <Button onClick={() => navigate("/finances")} className="w-full gap-2">
                    Go to Finance Center <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </ModuleShell>
  );
};

export default CareerPathModule;
